/**
 * Add-on Bundle Service (user-facing)
 *
 * Handles the public add-on bundle catalog and purchases. Admin bundle
 * management (CRUD) lives in adminService.ts.
 */

import { apiRequest } from '../utils/apiClient';
import { getAuthToken } from '../utils/authToken';
import type { Addon, AddonAudience, DiscountKind } from './adminService';

// Re-export shared types so pages have a single source of truth.
export type { Addon, AddonAudience, DiscountKind };

/**
 * Get the auth token from the persisted session.
 */
function getToken(): string {
  return getAuthToken();
}

/** A single add-on slot inside a bundle, with its quantity + add-on detail. */
export interface AddonBundleItem {
  id?: string;
  bundleId?: string;
  addonId: string;
  quantity: number;
  addon?: Pick<
    Addon,
    'id' | 'slug' | 'name' | 'nameAr' | 'family' | 'fulfillment' | 'priceSar' | 'isQuoteBased'
  >;
}

/** Computed pricing returned by the public bundle endpoints. */
export interface BundlePricing {
  baseSar: number;
  discountSar: number;
  finalSar: number;
  priceCoins: number;
}

/** A public add-on bundle, always returned with its `items` and `pricing`. */
export interface AddonBundle {
  id: string;
  slug: string;
  name: string;
  nameAr?: string | null;
  description?: string | null;
  descriptionAr?: string | null;
  discountKind: DiscountKind;
  discountValue: number;
  audiences: AddonAudience[];
  isActive: boolean;
  sortOrder: number;
  items: AddonBundleItem[];
  pricing?: BundlePricing;
  createdAt: string;
  updatedAt: string;
}

/**
 * Fetch the active bundle catalog with computed pricing + savings.
 */
export async function fetchBundles(): Promise<AddonBundle[]> {
  // Send the token (when signed in) so the audience gate returns the viewer's
  // bundles, not just the anonymous ALL set — mirrors fetchAddons.
  const response = await apiRequest<{ data: AddonBundle[] }>('/addon-bundles', {
    method: 'GET',
    token: getToken() || undefined,
  });
  return response.data;
}

/**
 * Fetch a single bundle by its slug (with computed pricing).
 */
export async function fetchBundle(slug: string): Promise<AddonBundle> {
  const response = await apiRequest<{ data: AddonBundle }>(`/addon-bundles/${slug}`, {
    method: 'GET',
  });
  return response.data;
}

/**
 * Purchase a bundle. SAR activates instantly; COINS debits coins (the backend
 * returns 409 INSUFFICIENT_COINS when the balance is too low).
 */
export async function purchaseBundle(
  slug: string,
  body: { pricingMethod?: 'SAR' | 'COINS'; propertyId?: string } = {},
): Promise<{ data: unknown; pricing: unknown }> {
  return await apiRequest<{ data: unknown; pricing: unknown }>(
    `/addon-bundles/${slug}/purchase`,
    { method: 'POST', token: getToken(), body },
  );
}

const bundleService = {
  fetchBundles,
  fetchBundle,
  purchaseBundle,
};

export default bundleService;
